import { AbstractMesh, Vector3 } from '@babylonjs/core';
import { AbstractMeshBehavior } from './AbstractMeshBehavior';

/**
 * Platform states for the crumbling behavior
 */
enum CrumblingState {
  Stable = 'stable',
  Triggered = 'triggered',
  Eroding = 'eroding',
  Falling = 'falling',
  Fallen = 'fallen',
}

/**
 * Configuration for crumbling platform behavior
 */
export interface CrumblingPlatformConfig {
  /** Seconds before erosion starts after player contact */
  triggerDelay: number;
  /** Seconds of shaking/erosion before fall */
  erosionDuration: number;
  /** Speed at which platform falls */
  fallSpeed: number;
  /** Time until respawn (0 = no respawn) */
  respawnTime: number;
}

/**
 * Default configuration for crumbling platforms
 */
export const DEFAULT_CRUMBLING_CONFIG: CrumblingPlatformConfig = {
  triggerDelay: 1.0,
  erosionDuration: 1.5,
  fallSpeed: 20,
  respawnTime: 0,
};

/**
 * A platform that crumbles and falls when the player stands on it
 */
export class CrumblingPlatformBehavior extends AbstractMeshBehavior {
  private state: CrumblingState = CrumblingState.Stable;
  private timer: number = 0;
  private originalPosition: Vector3 = Vector3.Zero();
  private shakeIntensity: number = 0;

  // Velocity for player to use (like MovingPlatformBehavior)
  public velocity: Vector3 = Vector3.Zero();

  constructor(private config: CrumblingPlatformConfig = DEFAULT_CRUMBLING_CONFIG) {
    super('crumblingPlatform');
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);

    // Store original position for shake effect and respawn
    this.originalPosition = this.mesh.position.clone();

    // Store reference in mesh metadata for easy access
    this.setMetaData('crumblingPlatform', this);
  }

  beforeRender(): void {
    if (!this.mesh || !this.level) return;

    const deltaTime = this.level.game.scene.getEngine().getDeltaTime() / 1000;
    if (deltaTime <= 0) return;

    const oldPos = this.mesh.position.clone();

    switch (this.state) {
      case CrumblingState.Stable:
        this.checkPlayerContact();
        break;

      case CrumblingState.Triggered:
        this.timer += deltaTime;
        if (this.timer >= this.config.triggerDelay) {
          this.startEroding();
        }
        break;

      case CrumblingState.Eroding:
        this.timer += deltaTime;
        this.applyShakeEffect(deltaTime);
        if (this.timer >= this.config.erosionDuration) {
          this.startFalling();
        }
        break;

      case CrumblingState.Falling:
        this.applyFalling(deltaTime);
        break;

      case CrumblingState.Fallen:
        if (this.config.respawnTime > 0) {
          this.timer += deltaTime;
          if (this.timer >= this.config.respawnTime) {
            this.respawn();
          }
        }
        break;
    }

    // Calculate velocity for player
    this.velocity = this.mesh.position.subtract(oldPos).scale(1 / deltaTime);

    // Sync physics body
    if (this.mesh.physicsBody) {
      this.mesh.physicsBody.disablePreStep = false;
    }

    // Check if player is on this platform
    this.checkPlayerOnPlatform();
  }

  /**
   * Check if player is touching this platform (triggers crumbling)
   */
  private checkPlayerContact(): void {
    const player = this.level.player;
    if (!player) return;

    const playerPos = player.getPosition();
    if (this.isPositionOnTop(playerPos) && player.grounded) {
      this.trigger();
    }
  }

  /**
   * Trigger the crumbling process
   */
  private trigger(): void {
    if (this.state !== CrumblingState.Stable) return;

    this.state = CrumblingState.Triggered;
    this.timer = 0;
  }

  /**
   * Start the erosion/shaking phase
   */
  private startEroding(): void {
    this.state = CrumblingState.Eroding;
    this.timer = 0;
    this.shakeIntensity = 0.05;
  }

  /**
   * Apply shake effect during erosion
   */
  private applyShakeEffect(deltaTime: number): void {
    // Increase shake intensity as erosion progresses
    const progress = this.timer / this.config.erosionDuration;
    this.shakeIntensity = 0.05 + progress * 0.15;

    // Random shake offset
    const shakeX = (Math.random() - 0.5) * 2 * this.shakeIntensity;
    const shakeZ = (Math.random() - 0.5) * 2 * this.shakeIntensity;

    this.mesh.position.x = this.originalPosition.x + shakeX;
    this.mesh.position.z = this.originalPosition.z + shakeZ;
  }

  /**
   * Start the falling phase
   */
  private startFalling(): void {
    this.state = CrumblingState.Falling;

    // Disable physics body so it doesn't collide while falling
    if (this.mesh.physicsBody) {
      this.mesh.physicsBody.setMassProperties({ mass: 0 });
    }
  }

  /**
   * Apply falling motion
   */
  private applyFalling(deltaTime: number): void {
    this.mesh.position.y -= this.config.fallSpeed * deltaTime;

    // Check if fallen out of level
    if (this.mesh.position.y < -50) {
      this.onFallen();
    }
  }

  /**
   * Called when platform has fully fallen
   */
  private onFallen(): void {
    this.state = CrumblingState.Fallen;
    this.timer = 0;

    if (this.config.respawnTime <= 0) {
      // No respawn - hide the mesh
      this.mesh.setEnabled(false);
    }
  }

  /**
   * Respawn the platform at original position
   */
  private respawn(): void {
    this.state = CrumblingState.Stable;
    this.timer = 0;
    this.mesh.position.copyFrom(this.originalPosition);
    this.mesh.setEnabled(true);

    // Re-enable physics
    if (this.mesh.physicsBody) {
      this.mesh.physicsBody.setMassProperties({ mass: 0 }); // Keep kinematic
    }
  }

  /**
   * Check if player is on this platform and update their reference
   */
  private checkPlayerOnPlatform(): void {
    const player = this.level.player;
    if (!player) return;

    const playerPos = player.getPosition();
    const isOnTop = this.isPositionOnTop(playerPos);

    if (isOnTop && player.grounded && this.state !== CrumblingState.Fallen) {
      // Player is on this platform - they'll move with it via velocity
      player.currentPlatform = this as any; // Same interface as MovingPlatformBehavior
    } else if ((player.currentPlatform as any) === this && !isOnTop) {
      player.currentPlatform = null;
    }
  }

  /**
   * Get platform info for player movement (same interface as MovingPlatformBehavior)
   */
  getPlatformInfo(): { velocity: Vector3; topY: number } {
    const bounds = this.mesh.getBoundingInfo().boundingBox;
    return {
      velocity: this.velocity.clone(),
      topY: bounds.maximumWorld.y,
    };
  }

  /**
   * Check if a position is on top of this platform
   */
  isPositionOnTop(position: Vector3, tolerance: number = 0.5): boolean {
    const bounds = this.mesh.getBoundingInfo().boundingBox;
    const min = bounds.minimumWorld;
    const max = bounds.maximumWorld;

    return (
      position.x >= min.x - tolerance &&
      position.x <= max.x + tolerance &&
      position.z >= min.z - tolerance &&
      position.z <= max.z + tolerance &&
      position.y >= max.y - tolerance &&
      position.y <= max.y + 2
    );
  }

  /**
   * Get current state for debugging/UI
   */
  getState(): CrumblingState {
    return this.state;
  }

  clone(): CrumblingPlatformBehavior {
    return new CrumblingPlatformBehavior({ ...this.config });
  }
}
